// FILE: CollegeStudentBulkUpload.jsx
import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Upload, FileSpreadsheet, AlertCircle, CheckCircle, X,
  Download, Users, CreditCard, Wallet, Info, ChevronDown, ChevronUp
} from 'lucide-react';
import * as XLSX from 'xlsx';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.theskillgenie.com';

// Required CSV columns - order matters
const REQUIRED_HEADERS = ['S No', 'Name', 'Email', 'Roll No', 'Branch', 'CollegeName', 'Semester'];

// --- helpers ----------------------------------------------------------------

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/** Build a CSV data-URL and trigger download. */
const downloadCSVFromString = (csvString, filename) => {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ----------------------------------------------------------------------------

const CollegeStudentBulkUpload = ({ collegeId, onUploadComplete, creditRefreshKey }) => {
  const [file, setFile]             = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError]           = useState(null);
  const [preview, setPreview]       = useState(null);
  const [credits, setCredits]       = useState(null);
  const [clientErrors, setClientErrors] = useState([]); // pre-upload validation issues
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // College name from localStorage (set at admin login)
  const collegeUser  = (() => { try { return JSON.parse(localStorage.getItem('user')) || {}; } catch { return {}; } })();
  const collegeName  = collegeUser.name || '';
  const uploadedBy   = collegeUser.username || localStorage.getItem('collegeUsername') || 'admin';

  // Re-fetch credits on mount, collegeId change, or whenever parent signals a refresh
  // (creditRefreshKey bumps after every student deletion)
  useEffect(() => {
    if (collegeId) fetchCredits();
  }, [collegeId, creditRefreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCredits = async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/credits/balance`, { college_id: collegeId });
      if (res.data.success) setCredits(res.data.credits);
    } catch (err) {
      console.error('Failed to fetch credits:', err);
    }
  };

  // -- Template downloads ---------------------------------------------------

  const downloadCSVTemplate = () => {
    const rows = [
      REQUIRED_HEADERS.join(','),
      `1,John Doe,john.doe@college.edu,CS2021001,Computer Science,${collegeName || 'My College'},5`,
      `2,Jane Smith,jane.smith@college.edu,EC2021002,Electronics,${collegeName || 'My College'},3`,
      `3,Raj Kumar,raj.kumar@college.edu,ME2021003,Mechanical,${collegeName || 'My College'},1`,
    ].join('\n');

    downloadCSVFromString(rows, 'student_bulk_upload_template.csv');
  };

  const downloadExcelTemplate = () => {
    const data = [
      REQUIRED_HEADERS,
      ['1', 'John Doe',   'john.doe@college.edu',   'CS2021001', 'Computer Science', collegeName || 'My College', '5'],
      ['2', 'Jane Smith', 'jane.smith@college.edu',  'EC2021002', 'Electronics',      collegeName || 'My College', '3'],
      ['3', 'Raj Kumar',  'raj.kumar@college.edu',   'ME2021003', 'Mechanical',       collegeName || 'My College', '1'],
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    // Set column widths
    ws['!cols'] = [{ wch: 5 }, { wch: 20 }, { wch: 30 }, { wch: 15 }, { wch: 20 }, { wch: 25 }, { wch: 10 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Students');
    XLSX.writeFile(wb, 'student_bulk_upload_template.xlsx');
  };

  // -- File parsing ---------------------------------------------------------

  const parseFileToRows = async (f) => {
    const ext = f.name.split('.').pop().toLowerCase();
    if (ext === 'csv') {
      const text = await f.text();
      return text
        .split('\n')
        .map(row => row.split(',').map(c => c.trim().replace(/^"|"$/g, '')))
        .filter(row => row.some(c => c !== ''));
    } else if (['xlsx', 'xls'].includes(ext)) {
      const buf = await f.arrayBuffer();
      const wb  = XLSX.read(buf, { type: 'array' });
      const ws  = wb.Sheets[wb.SheetNames[0]];
      return XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    }
    throw new Error('Unsupported file format. Upload CSV or Excel (.xlsx/.xls) only.');
  };

  // -- Client-side validation -----------------------------------------------

  const validateRows = (dataRows) => {
    const issues  = [];
    const seenEmails = new Set();
    const seenRolls  = new Set();

    dataRows.forEach((row, idx) => {
      const rowNum      = idx + 2; // +1 header, +1 to make 1-indexed
      const [, name, email, rollNo, branch, colName, semester] = row.map(c => String(c || '').trim());

      if (!name)     issues.push({ row: rowNum, field: 'Name',        reason: 'Name is required' });
      if (!email)    issues.push({ row: rowNum, field: 'Email',       reason: 'Email is required' });
      if (!rollNo)   issues.push({ row: rowNum, field: 'Roll No',     reason: 'Roll No is required' });
      if (!branch)   issues.push({ row: rowNum, field: 'Branch',      reason: 'Branch is required' });
      if (!colName)  issues.push({ row: rowNum, field: 'CollegeName', reason: 'CollegeName is required' });
      if (!semester) issues.push({ row: rowNum, field: 'Semester',    reason: 'Semester is required' });

      if (email && !isValidEmail(email)) {
        issues.push({ row: rowNum, field: 'Email', reason: `Invalid email format: ${email}` });
      }

      if (email && isValidEmail(email)) {
        const lc = email.toLowerCase();
        if (seenEmails.has(lc)) {
          issues.push({ row: rowNum, field: 'Email', reason: `Duplicate email within file: ${email}` });
        } else {
          seenEmails.add(lc);
        }
      }

      if (rollNo) {
        if (seenRolls.has(rollNo.toLowerCase())) {
          issues.push({ row: rowNum, field: 'Roll No', reason: `Duplicate roll number within file: ${rollNo}` });
        } else {
          seenRolls.add(rollNo.toLowerCase());
        }
      }

      // College name check (warn if different from admin's college)
      if (colName && collegeName && colName.toLowerCase() !== collegeName.toLowerCase()) {
        issues.push({
          row: rowNum,
          field: 'CollegeName',
          reason: `College name "${colName}" does not match your institution "${collegeName}"`,
        });
      }
    });

    return issues;
  };

  // -- File selection handler -----------------------------------------------

  const handleFileSelect = async (selectedFile) => {
    setError(null);
    setUploadResult(null);
    setPreview(null);
    setClientErrors([]);
    setShowAllErrors(false);

    if (!selectedFile) return;

    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    const nameOk = /\.(csv|xlsx|xls)$/i.test(selectedFile.name);
    if (!allowedTypes.includes(selectedFile.type) && !nameOk) {
      setError('Invalid file type. Please upload a CSV or Excel (.xlsx/.xls) file.');
      return;
    }

    try {
      const rows    = await parseFileToRows(selectedFile);
      const headers = (rows[0] || []).map(h => String(h).trim());

      // Validate headers
      const headersMatch = REQUIRED_HEADERS.every(
        (h, i) => headers[i]?.toLowerCase() === h.toLowerCase()
      );
      if (!headersMatch) {
        setError(
          `Invalid column headers. Expected exactly:\n${REQUIRED_HEADERS.join(' | ')}\n\nFound:\n${headers.join(' | ')}`
        );
        return;
      }

      const dataRows = rows.slice(1).filter(r => r.some(c => String(c).trim() !== ''));

      if (dataRows.length === 0) {
        setError('The file has no student data rows. Please fill in the template.');
        return;
      }

      // Client-side validation
      const issues = validateRows(dataRows);
      setClientErrors(issues);

      setPreview({ headers, rows: dataRows.slice(0, 5), totalRows: dataRows.length });
      setFile(selectedFile);
    } catch (err) {
      setError(err.message || 'Failed to parse file. Please check the format.');
    }
  };

  // -- Drag & drop ----------------------------------------------------------

  const handleDragEnter  = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true);  }, []);
  const handleDragLeave  = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }, []);
  const handleDragOver   = useCallback((e) => { e.preventDefault(); e.stopPropagation(); }, []);
  const handleDrop       = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileSelect(f);
  }, [collegeName]); // eslint-disable-line react-hooks/exhaustive-deps

  // -- Upload ---------------------------------------------------------------

  const handleUpload = async () => {
    if (!file || !collegeId) return;

    setUploading(true);
    setError(null);
    setUploadResult(null);
    setUploadProgress(10);

    try {
      const rows     = await parseFileToRows(file);
      const dataRows = rows.slice(1).filter(r => r.some(c => String(c).trim() !== ''));

      setUploadProgress(30);

      const students = dataRows.map(row => ({
        name:         String(row[1] || '').trim(),
        email:        String(row[2] || '').trim(),
        roll_number:  String(row[3] || '').trim(),
        branch:       String(row[4] || '').trim(),
        college_name: String(row[5] || '').trim(),
        semester:     String(row[6] || '').trim(),
      }));

      setUploadProgress(50);

      const res = await axios.post(`${API_BASE_URL}/students/bulk-upload`, {
        college_id:  collegeId,
        students,
        uploaded_by: uploadedBy,
        file_name:   file.name,
      });

      setUploadProgress(100);

      if (res.data && typeof res.data === 'object') {
        // Backend nests counts inside `summary`; normalise to a flat object for the UI.
        const d = res.data;
        const s = d.summary || {};
        const normalized = {
          upload_log_id:    d.upload_log_id,
          status:           d.status,                                   // 'COMPLETED' | 'PARTIAL' | 'FAILED'
          total:            s.total            ?? d.total            ?? 0,
          successful_count: (s.created_count   ?? d.successful_count ?? 0)
                          + (s.restored_count  ?? d.restored_count   ?? 0),
          created_count:    s.created_count    ?? d.created_count    ?? 0,
          restored_count:   s.restored_count   ?? d.restored_count   ?? 0,
          duplicate_count:  s.duplicate_count  ?? d.duplicate_count  ?? 0,
          failed_count:     (s.failed_count    ?? d.failed_count     ?? 0)
                          + (s.duplicate_count ?? d.duplicate_count  ?? 0),
          emails_sent:      d.emails_sent      ?? 0,
          email_failures:   d.email_failures   ?? 0,
          credits_remaining: s.credits_remaining ?? d.credits_remaining,
          errors:           d.errors,
        };
        setUploadResult(normalized);
        fetchCredits();

        if (normalized.successful_count > 0) {
          setFile(null);
          setPreview(null);
          setClientErrors([]);
          if (onUploadComplete) onUploadComplete();
        }
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (err) {
      console.error('Upload error:', err);
      if (err.response?.data?.error === 'INSUFFICIENT_CREDITS') {
        setError({
          type:     'INSUFFICIENT_CREDITS',
          message:  err.response.data.message,
          required: err.response.data.required,
          available: err.response.data.available,
          redirect: err.response.data.redirect,
        });
      } else {
        setError(err.response?.data?.message || err.message || 'Upload failed. Please try again.');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // -- Error report download -------------------------------------------------

  const downloadErrorReport = async (uploadLogId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/students/error-report/${uploadLogId}`);
      if (res.data?.csv_data) {
        downloadCSVFromString(res.data.csv_data, `error_report_upload_${uploadLogId}.csv`);
      }
    } catch (err) {
      console.error('Failed to download error report:', err);
      alert('Could not download error report. Please try again.');
    }
  };

  // -- Reset -----------------------------------------------------------------

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    setClientErrors([]);
    setUploadResult(null);
    setShowAllErrors(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // -- Summary stats from result ---------------------------------------------

  // Use the backend-authoritative status field first; fall back to count-based logic.
  const resultStatusColor = uploadResult
    ? uploadResult.status === 'COMPLETED'
      ? 'green'
      : uploadResult.status === 'FAILED'
        ? 'red'
        : uploadResult.status === 'PARTIAL'
          ? 'yellow'
          : uploadResult.failed_count === 0
            ? 'green'
            : uploadResult.successful_count === 0
              ? 'red'
              : 'yellow'
    : null;

  const VISIBLE_ERROR_COUNT = 5;

  // -- Render ----------------------------------------------------------------

  // Drop zone dynamic styles
  const dropZoneStyle = {
    borderRadius: '14px',
    border: '2px dashed',
    padding: '40px 24px',
    textAlign: 'center',
    cursor: file ? 'default' : 'pointer',
    transition: 'all 0.2s ease',
    ...(isDragging
      ? { borderColor: 'var(--brand-primary, var(--primary-blue, #4F46E5))', background: 'rgba(79, 70, 229,0.05)' }
      : file
        ? { borderColor: '#10B981', background: 'rgba(16,185,129,0.03)' }
        : { borderColor: '#D1D5DB', background: '#FAFAFA' }
    ),
  };

  return (
    <div style={{ maxWidth: 896, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* -- Header row -- */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 46, height: 46, borderRadius: '12px',
            background: 'rgba(79, 70, 229,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Users size={22} style={{ color: 'var(--brand-primary, var(--primary-blue, #4F46E5))' }} />
          </div>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0F172A', margin: 0, letterSpacing: '-0.01em' }}>
              Bulk Student Upload
            </h2>
            <p style={{ fontSize: '0.8rem', color: '#64748B', margin: '2px 0 0' }}>
              Upload CSV or Excel to add multiple students at once
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          {credits !== null && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '6px 14px',
              background: '#EFF6FF',
              border: '1px solid #BFDBFE',
              borderRadius: '999px',
              color: 'var(--secondary-blue, #1E3A8A)',
              fontSize: '0.8rem', fontWeight: 700,
            }}>
              <Wallet size={15} />
              {credits.available} Credits Available
            </div>
          )}

          <button onClick={downloadCSVTemplate}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '7px 14px',
              background: '#FFFFFF',
              border: '1px solid var(--border-subtle, #E2E8F0)',
              borderRadius: '9px',
              fontSize: '0.8rem', fontWeight: 600, color: '#374151',
              cursor: 'pointer', transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
            onMouseLeave={e => e.currentTarget.style.background = '#FFFFFF'}
          >
            <Download size={14} style={{ color: '#16A34A' }} />
            CSV Template
          </button>
          <button onClick={downloadExcelTemplate}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '7px 14px',
              background: '#FFFFFF',
              border: '1px solid var(--border-subtle, #E2E8F0)',
              borderRadius: '9px',
              fontSize: '0.8rem', fontWeight: 600, color: '#374151',
              cursor: 'pointer', transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
            onMouseLeave={e => e.currentTarget.style.background = '#FFFFFF'}
          >
            <Download size={14} style={{ color: 'var(--primary-blue, #4F46E5)' }} />
            Excel Template
          </button>
        </div>
      </div>

      {/* -- Instructions banner -- */}
      <div style={{
        background: 'rgba(79, 70, 229,0.04)',
        border: '1px solid rgba(79, 70, 229,0.15)',
        borderRadius: '12px',
        padding: '16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'rgba(79, 70, 229,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, marginTop: 1,
          }}>
            <Info size={14} style={{ color: 'var(--primary-blue, #4F46E5)' }} />
          </div>
          <div style={{ fontSize: '0.85rem', color: '#1E3A8A' }}>
            <p style={{ fontWeight: 700, margin: '0 0 6px', color: 'var(--secondary-blue, #1E3A8A)' }}>Upload Instructions</p>
            <ul style={{ margin: 0, paddingLeft: 18, color: '#3B5BDB', lineHeight: 1.8 }}>
              <li>Download the template and fill in student details exactly.</li>
              <li>Required columns: <strong style={{ color: 'var(--secondary-blue, #1E3A8A)' }}>{REQUIRED_HEADERS.join(' | ')}</strong></li>
              <li>The <strong>CollegeName</strong> column must match your institution name.</li>
              <li>Each successful student creation deducts <strong>1 credit</strong>.</li>
              <li>Duplicate emails and roll numbers are automatically rejected.</li>
              <li>Two emails are sent per student: a <strong>Welcome Email</strong> and a separate <strong>Login Credentials Email</strong>.</li>
              <li>Default password = student's roll number (students must change it on first login).</li>
            </ul>
          </div>
        </div>
      </div>

      {/* -- Drop zone -- */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={dropZoneStyle}
        onClick={() => !file && fileInputRef.current?.click()}
        onMouseEnter={e => { if (!file && !isDragging) { e.currentTarget.style.borderColor = 'rgba(79, 70, 229,0.4)'; e.currentTarget.style.background = 'rgba(79, 70, 229,0.02)'; } }}
        onMouseLeave={e => { if (!file && !isDragging) { e.currentTarget.style.borderColor = '#D1D5DB'; e.currentTarget.style.background = '#FAFAFA'; } }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={(e) => handleFileSelect(e.target.files[0])}
          style={{ display: 'none' }}
        />

        {!file ? (
          <>
            <div style={{
              width: 64, height: 64, borderRadius: '16px',
              background: 'rgba(79, 70, 229,0.07)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <Upload size={30} style={{ color: 'var(--primary-blue, #4F46E5)' }} />
            </div>
            <p style={{ fontSize: '1rem', fontWeight: 700, color: '#374151', margin: '0 0 4px' }}>
              Drag &amp; drop your file here
            </p>
            <p style={{ fontSize: '0.875rem', color: '#6B7280', margin: '0 0 10px' }}>or click to browse</p>
            <p style={{ fontSize: '0.75rem', color: '#9CA3AF', margin: 0 }}>Supported: CSV, XLSX, XLS</p>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
            <FileSpreadsheet style={{ color: '#10B981', flexShrink: 0 }} size={40} />
            <div style={{ textAlign: 'left' }}>
              <p style={{ fontWeight: 700, color: '#0F172A', margin: '0 0 3px', fontSize: '0.95rem' }}>{file.name}</p>
              <p style={{ fontSize: '0.8rem', color: '#64748B', margin: 0 }}>
                {preview?.totalRows ?? 0} student rows detected
              </p>
              {clientErrors.length > 0 && (
                <p style={{ fontSize: '0.75rem', color: '#D97706', margin: '4px 0 0', fontWeight: 600 }}>
                  {clientErrors.length} validation warning{clientErrors.length !== 1 ? 's' : ''} - review before uploading
                </p>
              )}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); clearFile(); }}
              style={{
                marginLeft: 8, padding: '8px', borderRadius: '50%',
                border: 'none', background: 'transparent', cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              title="Remove file"
            >
              <X size={20} style={{ color: '#EF4444' }} />
            </button>
          </div>
        )}
      </div>

      {/* -- Client-side validation warnings -- */}
      {clientErrors.length > 0 && (
        <div style={{
          border: '1px solid #FCD34D',
          borderRadius: '12px',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '12px 16px',
            background: '#FFFBEB',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: '1px solid #FCD34D',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertCircle size={17} style={{ color: '#D97706' }} />
              <span style={{ fontWeight: 700, color: '#92400E', fontSize: '0.875rem' }}>
                {clientErrors.length} Pre-Upload Validation Warning{clientErrors.length !== 1 ? 's' : ''}
              </span>
            </div>
            <button
              onClick={() => setShowAllErrors(v => !v)}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                color: '#B45309', fontSize: '0.8rem', fontWeight: 600,
                background: 'none', border: 'none', cursor: 'pointer',
              }}
            >
              {showAllErrors ? <><ChevronUp size={15} /> Show Less</> : <><ChevronDown size={15} /> Show All</>}
            </button>
          </div>
          <div>
            {(showAllErrors ? clientErrors : clientErrors.slice(0, VISIBLE_ERROR_COUNT)).map((e, i) => (
              <div key={i} style={{
                padding: '9px 16px',
                display: 'flex', alignItems: 'flex-start', gap: 12,
                fontSize: '0.8rem',
                background: i % 2 === 0 ? '#FFFBEB' : '#FFF8E1',
                borderBottom: '1px solid rgba(252,211,77,0.4)',
              }}>
                <span style={{ color: '#D97706', fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>Row {e.row}</span>
                <span style={{ color: '#B45309', fontWeight: 600 }}>[{e.field}]</span>
                <span style={{ color: '#78350F' }}>{e.reason}</span>
              </div>
            ))}
            {!showAllErrors && clientErrors.length > VISIBLE_ERROR_COUNT && (
              <div style={{ padding: '9px 16px', fontSize: '0.8rem', color: '#B45309', fontWeight: 600, background: '#FFFBEB' }}>
                ... and {clientErrors.length - VISIBLE_ERROR_COUNT} more. Click "Show All" to expand.
              </div>
            )}
          </div>
        </div>
      )}

      {/* -- Preview table -- */}
      {preview && (
        <div>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#374151', margin: '0 0 10px' }}>
            Preview (first {preview.rows.length} of {preview.totalRows} rows)
          </h3>
          <div style={{ overflowX: 'auto', border: '1px solid var(--border-subtle, #E2E8F0)', borderRadius: '10px', background: '#FFFFFF' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead style={{ background: '#F8FAFC' }}>
                <tr>
                  {preview.headers.map((h, i) => (
                    <th key={i} style={{
                      padding: '10px 12px', textAlign: 'left',
                      fontWeight: 700, color: '#374151', whiteSpace: 'nowrap',
                      borderBottom: '1px solid var(--border-subtle, #E2E8F0)',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row, ri) => (
                  <tr key={ri} style={{ borderTop: '1px solid var(--border-subtle, #E2E8F0)' }}>
                    {preview.headers.map((_, ci) => (
                      <td key={ci} style={{ padding: '9px 12px', color: '#4B5563' }}>{String(row[ci] ?? '-')}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* -- Upload error -- */}
      {error && (
        <div style={{
          padding: '16px 20px',
          background: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <AlertCircle style={{ color: '#DC2626', flexShrink: 0, marginTop: 2 }} size={19} />
            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: 700, color: '#991B1B', margin: '0 0 4px', fontSize: '0.9rem' }}>
                {typeof error === 'object' && error.type === 'INSUFFICIENT_CREDITS'
                  ? 'Insufficient Credits'
                  : 'Upload Failed'}
              </p>
              <p style={{ fontSize: '0.85rem', color: '#B91C1C', margin: 0, whiteSpace: 'pre-line' }}>
                {typeof error === 'object' ? error.message : error}
              </p>
              {typeof error === 'object' && error.type === 'INSUFFICIENT_CREDITS' && (
                <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#B91C1C' }}>
                  Required: <strong>{error.required}</strong> credits &nbsp;|&nbsp; Available: <strong>{error.available}</strong> credits
                </div>
              )}
              {typeof error === 'object' && error.type === 'INSUFFICIENT_CREDITS' && (
                <button
                  onClick={() => window.location.href = error.redirect}
                  style={{
                    marginTop: 12, display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 16px',
                    background: '#DC2626', color: '#FFFFFF',
                    borderRadius: '10px', border: 'none', cursor: 'pointer',
                    fontSize: '0.85rem', fontWeight: 600, transition: 'opacity 0.15s',
                  }}
                >
                  <CreditCard size={14} />
                  Purchase Credits
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* -- Upload progress -- */}
      {uploading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, background: '#E2E8F0', borderRadius: '999px', height: 8, overflow: 'hidden' }}>
              <div
                style={{
                  background: 'var(--brand-primary, var(--primary-blue, #4F46E5))',
                  height: '100%',
                  borderRadius: '999px',
                  width: `${uploadProgress}%`,
                  transition: 'width 0.5s ease',
                }}
              />
            </div>
            <span style={{ fontSize: '0.8rem', color: '#64748B', fontWeight: 700, minWidth: 36 }}>{uploadProgress}%</span>
          </div>
          <p style={{ textAlign: 'center', color: '#64748B', fontSize: '0.875rem', animation: 'pulse 1.5s infinite', margin: 0 }}>
            Processing students - please wait...
          </p>
        </div>
      )}

      {/* -- Upload result -- */}
      {uploadResult && (
        <div style={{
          border: resultStatusColor === 'green' ? '1px solid #BBF7D0' : resultStatusColor === 'yellow' ? '1px solid #FDE68A' : '1px solid #FECACA',
          borderRadius: '14px',
          overflow: 'hidden',
        }}>
          {/* Summary header */}
          <div style={{
            padding: '16px 20px',
            display: 'flex', alignItems: 'center', gap: 12,
            background: resultStatusColor === 'green' ? '#DCFCE7' : resultStatusColor === 'yellow' ? '#FEF9C3' : '#FEF2F2',
          }}>
            {resultStatusColor === 'green'
              ? <CheckCircle style={{ color: '#16A34A' }} size={24} />
              : <AlertCircle style={{ color: resultStatusColor === 'yellow' ? '#CA8A04' : '#DC2626' }} size={24} />
            }
            <p style={{ fontWeight: 800, color: '#0F172A', fontSize: '1rem', margin: 0 }}>
              {resultStatusColor === 'green'
                ? `Upload Complete - ${uploadResult.restored_count > 0 ? `${uploadResult.created_count} added, ${uploadResult.restored_count} restored` : 'All students added'}!`
                : resultStatusColor === 'yellow'
                  ? `Partially Uploaded - ${uploadResult.successful_count} succeeded, ${uploadResult.failed_count} rejected`
                  : 'Upload Failed - No students were added'}
            </p>
          </div>

          {/* Stats grid */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            gap: 12, padding: '16px 20px',
            background: resultStatusColor === 'green' ? 'rgba(220,252,231,0.3)' : resultStatusColor === 'yellow' ? 'rgba(254,249,195,0.3)' : 'rgba(254,242,242,0.3)',
          }}>
            {[
              { value: uploadResult.total ?? 0,                           label: 'Total Rows',         color: '#0F172A' },
              { value: uploadResult.created_count ?? 0,                   label: 'New Students',       color: '#16A34A' },
              { value: uploadResult.restored_count ?? 0,                  label: 'Restored',           color: '#0891B2' },
              { value: uploadResult.failed_count ?? 0,                    label: 'Rejected Rows',      color: uploadResult.failed_count > 0 ? '#DC2626' : '#94A3B8' },
              { value: uploadResult.emails_sent ?? 0,                     label: 'Emails Sent',        color: '#059669' },
              { value: uploadResult.credits_remaining ?? credits?.available ?? '-', label: 'Credits Remaining', color: 'var(--primary-blue, #4F46E5)' },
            ].map((stat, i) => (
              <div key={i} style={{
                background: '#FFFFFF',
                borderRadius: '10px',
                padding: '14px 12px',
                border: '1px solid var(--border-subtle, #E2E8F0)',
                textAlign: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}>
                <p style={{ fontSize: '1.5rem', fontWeight: 800, color: stat.color, margin: '0 0 4px', lineHeight: 1 }}>{stat.value}</p>
                <p style={{ fontSize: '0.7rem', color: '#6B7280', margin: 0, fontWeight: 600 }}>{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Row-level errors */}
          {uploadResult.errors && uploadResult.errors.length > 0 && (
            <div style={{ padding: '0 20px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <p style={{ fontWeight: 700, color: '#374151', fontSize: '0.875rem', margin: 0 }}>
                  Rejected Rows ({uploadResult.errors.length})
                </p>
                {uploadResult.upload_log_id && (
                  <button
                    onClick={() => downloadErrorReport(uploadResult.upload_log_id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '6px 12px',
                      background: '#374151', color: '#FFFFFF',
                      borderRadius: '8px', border: 'none', cursor: 'pointer',
                      fontSize: '0.75rem', fontWeight: 700, transition: 'opacity 0.15s',
                    }}
                  >
                    <Download size={12} />
                    Download Error Report
                  </button>
                )}
              </div>
              <div style={{ border: '1px solid var(--border-subtle, #E2E8F0)', borderRadius: '10px', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead style={{ background: '#F8FAFC' }}>
                    <tr>
                      {['Row', 'Student', 'Email', 'Reason'].map(h => (
                        <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontSize: '0.7rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody style={{ background: '#FFFFFF' }}>
                    {uploadResult.errors.map((e, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--border-subtle, #E2E8F0)' }}>
                        <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#374151', fontWeight: 600 }}>{e.row}</td>
                        <td style={{ padding: '9px 12px', color: '#374151' }}>{e.student}</td>
                        <td style={{ padding: '9px 12px', color: '#6B7280', fontSize: '0.75rem' }}>{e.email || '-'}</td>
                        <td style={{ padding: '9px 12px', color: '#DC2626' }}>{e.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ padding: '0 20px 20px', display: 'flex', gap: 10 }}>
            <button
              onClick={() => { setUploadResult(null); }}
              style={{
                padding: '9px 18px',
                border: '1px solid var(--border-subtle, #E2E8F0)',
                borderRadius: '10px',
                background: '#FFFFFF', cursor: 'pointer',
                fontSize: '0.875rem', fontWeight: 600, color: '#374151',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
              onMouseLeave={e => e.currentTarget.style.background = '#FFFFFF'}
            >
              Upload Another File
            </button>
            {uploadResult.upload_log_id && uploadResult.failed_count > 0 && (
              <button
                onClick={() => downloadErrorReport(uploadResult.upload_log_id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '9px 18px',
                  background: 'var(--brand-primary, var(--primary-blue, #4F46E5))', color: '#FFFFFF',
                  borderRadius: '10px', border: 'none', cursor: 'pointer',
                  fontSize: '0.875rem', fontWeight: 700, transition: 'opacity 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                <Download size={14} />
                Download Error Report CSV
              </button>
            )}
          </div>
        </div>
      )}

      {/* -- Upload button -- */}
      {file && !uploading && !uploadResult && (
        <button
          onClick={handleUpload}
          disabled={clientErrors.some(e => e.reason.startsWith('Duplicate') || e.reason.startsWith('Invalid email'))}
          style={{
            width: '100%', padding: '14px 24px',
            background: clientErrors.some(e => e.reason.startsWith('Duplicate') || e.reason.startsWith('Invalid email'))
              ? '#CBD5E1'
              : 'var(--brand-primary, var(--primary-blue, #4F46E5))',
            color: '#FFFFFF',
            borderRadius: '12px', border: 'none',
            fontWeight: 800, fontSize: '1rem',
            cursor: clientErrors.some(e => e.reason.startsWith('Duplicate') || e.reason.startsWith('Invalid email'))
              ? 'not-allowed'
              : 'pointer',
            letterSpacing: '-0.01em',
            transition: 'opacity 0.15s',
            boxShadow: '0 2px 8px rgba(79, 70, 229,0.2)',
          }}
          onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.opacity = '0.9'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
        >
          Upload {preview?.totalRows ?? 0} Students
        </button>
      )}

      {file && !uploading && !uploadResult && clientErrors.some(e =>
        e.reason.startsWith('Duplicate') || e.reason.startsWith('Invalid email')
      ) && (
        <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#DC2626', margin: 0, fontWeight: 600 }}>
          Fix the duplicate or invalid-email errors above before uploading.
        </p>
      )}
    </div>
  );
};

export default CollegeStudentBulkUpload;


